// components/employee-tree-filter/EmployeeTreeFilter.tsx
import { useEffect, useState } from 'react'
import { Loader2, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { UserPickerDialog } from '@/components/user-select'
import { usersApi, type UserListItem, type UserSelectItem, userSelectItemToListItem } from '@/api/users'
import { showError } from '@/lib/error'
import type { DepartmentScopeValue } from '@/types/department'

export interface EmployeeTreeFilterProps {
  value?: number[]
  onChange?: (userIds: number[]) => void
  defaultDepartmentId?: number
  placeholder?: string
  label?: string
  disabled?: boolean
  /** 隐藏内嵌的部门范围选择框（选人弹窗内仍可自由切换部门） */
  hideDepartmentScope?: boolean
}

const loadSelectedUsers = async (ids: number[]): Promise<UserSelectItem[]> => {
  const pageSize = 100
  const users: UserSelectItem[] = []
  for (let i = 0; i < ids.length; i += pageSize) {
    const batch = ids.slice(i, i + pageSize)
    const res = await usersApi.selectUsers({ user_ids: batch, page_size: pageSize })
    users.push(...res.list)
  }
  return users
}

export function EmployeeTreeFilter({
  value = [],
  onChange,
  defaultDepartmentId,
  placeholder = '选择员工',
  label = '员工',
  disabled = false,
  hideDepartmentScope = false,
}: EmployeeTreeFilterProps) {
  const [departmentScope, setDepartmentScope] = useState<DepartmentScopeValue>({
    scope: defaultDepartmentId ? 'department' : 'my',
    department_id: defaultDepartmentId,
  })
  const [selectedUsers, setSelectedUsers] = useState<UserListItem[]>([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  // 外部 value 与内部 selectedUsers 保持同步
  useEffect(() => {
    const valueIds = [...value].sort((a, b) => a - b)
    const selectedIds = selectedUsers.map((u) => u.id).sort((a, b) => a - b)

    if (valueIds.length === 0) {
      if (selectedUsers.length > 0) {
        setSelectedUsers([])
      }
      return
    }

    if (valueIds.length !== selectedIds.length || !valueIds.every((id, index) => id === selectedIds[index])) {
      setIsLoadingUsers(true)
      loadSelectedUsers(valueIds)
        .then((users) => {
          setSelectedUsers(users.map(userSelectItemToListItem))
        })
        .catch((err) => {
          showError(err, '加载已选员工失败')
        })
        .finally(() => {
          setIsLoadingUsers(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleConfirm = (users: UserListItem[]) => {
    setSelectedUsers(users)
    onChange?.(users.map((u) => u.id))
  }

  const handleRemove = (userId: number) => {
    const next = selectedUsers.filter((u) => u.id !== userId)
    setSelectedUsers(next)
    onChange?.(next.map((u) => u.id))
  }

  const displayText = isLoadingUsers
    ? '加载中...'
    : selectedUsers.length === 0
      ? placeholder
      : `已选 ${selectedUsers.length} 人`

  return (
    <div className="space-y-1.5">
      {label ? <label className="text-sm font-medium">{label}</label> : null}
      <div className="flex flex-col gap-2">
        {!hideDepartmentScope && (
          <DepartmentScopeFilter
            value={departmentScope}
            onChange={(scopeValue) => {
              setDepartmentScope(scopeValue)
              setSelectedUsers([])
              onChange?.([])
            }}
          />
        )}
        <UserPickerDialog
          multiple
          departmentId={hideDepartmentScope ? null : departmentScope.department_id}
          value={selectedUsers}
          onConfirm={handleConfirm}
          title={label ? `选择${label}` : '选择员工'}
          confirmText="确定"
          trigger={
            <Button
              type="button"
              variant="outline"
              disabled={disabled || isLoadingUsers}
              className="h-auto min-h-10 w-full justify-start font-normal"
            >
              {isLoadingUsers ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
              )}
              <span className={selectedUsers.length === 0 ? 'text-muted-foreground' : ''}>
                {displayText}
              </span>
            </Button>
          }
        />
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedUsers.map((user) => (
              <Badge
                key={user.id}
                variant="secondary"
                className={disabled ? 'gap-1 pr-1' : 'cursor-pointer gap-1 pr-1'}
                onClick={disabled ? undefined : () => handleRemove(user.id)}
              >
                {user.nickname || user.username}
                {!disabled && <span className="text-xs">×</span>}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
